import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import BulkImportStaffModal from '@/components/feature/BulkImportStaffModal';

export default function InfirmiersPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [loading, setLoading] = useState(false);
  const [infirmiers, setInfirmiers] = useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ nom: '', prenom: '', email: '', telephone: '', motDePasse: '', specialite: '' });
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'super_admin' && role !== 'admin') { navigate('/', { replace: true }); return; }
    loadInfirmiers();
  }, [user, role, authLoading]);

  const loadInfirmiers = async () => {
    setLoading(true);
    const { data } = await supabase.from('infirmiers').select('*').order('created_at', { ascending: false });
    if (data) setInfirmiers(data);
    setLoading(false);
  };

  const createInfirmier = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingCreate(true);
    setError('');
    setSuccess('');
    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) throw new Error('Session expirée');

      const response = await supabase.functions.invoke('create-infirmier', {
        body: {
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone || null,
          motDePasse: formData.motDePasse,
          specialite: formData.specialite || null,
        },
        headers: { Authorization: `Bearer ${authData.session.access_token}` },
      });

      if (response.error) throw response.error;
      setSuccess('Infirmier créé avec succès !');
      setFormData({ nom: '', prenom: '', email: '', telephone: '', motDePasse: '', specialite: '' });
      setShowCreateForm(false);
      loadInfirmiers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoadingCreate(false);
    }
  };

  const toggleStatus = async (id: string, current: boolean) => {
    try {
      const newStatus = !current;
      await supabase.from('infirmiers').update({ actif: newStatus }).eq('id', id);
      setInfirmiers((prev) => prev.map((i) => (i.id === id ? { ...i, actif: newStatus } : i)));
      setSuccess(newStatus ? 'Infirmier activé' : 'Infirmier désactivé');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Erreur lors de la modification');
      setTimeout(() => setError(''), 3000);
    }
  };

  const deleteInfirmier = async (infirmierId: string) => {
    if (!confirm('Supprimer cet infirmier ? Cette action est irréversible.')) return;
    try {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) throw new Error('Session expirée');
      const response = await supabase.functions.invoke('delete-infirmier', {
        body: { infirmierId },
        headers: { Authorization: `Bearer ${authData.session.access_token}` },
      });
      if (response.error) throw response.error;
      setSuccess('Infirmier supprimé');
      loadInfirmiers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err?.context?.error || err?.message || 'Erreur suppression');
      setTimeout(() => setError(''), 5000);
    }
  };

  const filtered = infirmiers.filter((i) => {
    const s = searchQuery.toLowerCase();
    return i.nom.toLowerCase().includes(s) || i.prenom.toLowerCase().includes(s) || i.email.toLowerCase().includes(s);
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center"><i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i><p>Chargement...</p></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-xl border-2 border-teal-100 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Gestion des Infirmiers</h1>
                <p className="text-gray-600 mt-2">Créez et gérez les comptes des infirmiers</p>
              </div>
              <button onClick={() => navigate('/admin')} className="px-4 py-2 text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                ← Retour au tableau de bord
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-2xl flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 w-5 h-5 flex items-center justify-center"></i><span>{error}</span>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-600 rounded-2xl flex items-start gap-2">
                <i className="ri-check-line mt-0.5 w-5 h-5 flex items-center justify-center"></i><span>{success}</span>
              </div>
            )}

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Liste des infirmiers ({infirmiers.length})</h2>
              <div className="flex gap-3">
                <button onClick={() => setShowImportModal(true)} className="px-6 py-3 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all font-semibold whitespace-nowrap cursor-pointer">
                  <i className="ri-file-upload-line mr-2"></i>Importer (CSV)
                </button>
                <button onClick={() => setShowCreateForm(true)} className="px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold whitespace-nowrap cursor-pointer">
                  <i className="ri-add-line mr-2"></i>Créer un compte infirmier
                </button>
              </div>
            </div>

            {showCreateForm && (
              <div className="mb-8 p-6 bg-teal-50 border-2 border-teal-200 rounded-2xl">
                <h3 className="text-lg font-semibold mb-4">Nouveau compte infirmier</h3>
                <form onSubmit={createInfirmier} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nom *</label>
                      <input type="text" required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Prénom *</label>
                      <input type="text" required value={formData.prenom} onChange={(e) => setFormData({ ...formData, prenom: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Téléphone</label>
                      <input type="tel" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Spécialité</label>
                    <input type="text" value={formData.specialite} onChange={(e) => setFormData({ ...formData, specialite: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm" placeholder="Ex: Soins généraux, Pédiatrie..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Mot de passe *</label>
                    <input type="password" required minLength={10} value={formData.motDePasse} onChange={(e) => setFormData({ ...formData, motDePasse: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm" placeholder="Minimum 10 caractères" />
                  </div>
                  <div className="flex gap-4">
                    <button type="submit" disabled={loadingCreate} className="px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold disabled:opacity-50 whitespace-nowrap cursor-pointer">
                      {loadingCreate ? 'Création...' : 'Créer le compte'}
                    </button>
                    <button type="button" onClick={() => setShowCreateForm(false)} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all font-semibold whitespace-nowrap cursor-pointer">Annuler</button>
                  </div>
                </form>
              </div>
            )}

            <div className="mb-6">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                <input type="text" placeholder="Rechercher un infirmier..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:border-teal-500 text-sm" />
              </div>
            </div>

            <div className="space-y-4">
              {filtered.length === 0 ? (
                <div className="text-center py-12 text-gray-500">{searchQuery ? 'Aucun infirmier trouvé' : 'Aucun infirmier créé'}</div>
              ) : (
                filtered.map((inf: any) => (
                  <div key={inf.id} className="border-2 border-gray-100 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                          <i className="ri-nurse-line text-3xl text-teal-600"></i>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">{inf.nom} {inf.prenom}</h3>
                          <p className="text-gray-600">{inf.email}</p>
                          {inf.specialite && <p className="text-sm text-teal-600 font-medium mt-1">{inf.specialite}</p>}
                          {inf.telephone && <p className="text-sm text-gray-500"><i className="ri-phone-line mr-1"></i>{inf.telephone}</p>}
                          <p className="text-sm text-gray-500">Créé le {new Date(inf.created_at).toLocaleDateString('fr-FR')}</p>
                          {!inf.actif && <span className="inline-block mt-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">Compte désactivé</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => toggleStatus(inf.id, inf.actif)} className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${inf.actif ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                          <i className={`${inf.actif ? 'ri-lock-line' : 'ri-lock-unlock-line'} mr-1`}></i>
                          {inf.actif ? 'Désactiver' : 'Activer'}
                        </button>
                        {isTrueSuperAdmin && (
                          <button onClick={() => deleteInfirmier(inf.id)} className="px-4 py-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 font-medium transition-all whitespace-nowrap cursor-pointer">
                            <i className="ri-delete-bin-line mr-1"></i>Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <BulkImportStaffModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        staffType="infirmier"
        onSuccess={loadInfirmiers}
      />
      <Footer />
    </div>
  );
}